import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  Modal,
  ProductCardV2,
  theme,
  TransparentModal,
  useResponsive,
  useSafeFocusEffect,
  useSafeIsFocused,
  useSafeNavigation,
  useSafeRouteParams,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTrackEffect } from '@order/Telemetry';

import { LoadingAnimation, NotFoundView } from '../../../../components';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { useLastValidOrder } from '../../../../context/Order';
import { useNavigateBack } from '../../../../navigation';
import { useOrderProductConfirmProductScreenData } from './hooks/useOrderProductConfirmProductScreenData';

export const OrderProductConfirmProductScreen = () => {
  const { formatMessage } = useIntl();

  const { productSlug } = useSafeRouteParams<{ productSlug?: string }>();

  const isFocused = useSafeIsFocused();

  const { baseProduct, isFetchingBaseProduct } = useOrderProductConfirmProductScreenData({
    productSlug: productSlug ?? '',
  });

  if (!isFocused) return null;

  if (isFetchingBaseProduct) {
    return (
      <OrderProductConfirmProductContainer>
        <Modal.Row style={styles.notLoadedViewContainer}>
          <LoadingAnimation size="large" />
        </Modal.Row>
      </OrderProductConfirmProductContainer>
    );
  }

  if (!baseProduct?.isDeepLinkingEnabled) {
    return (
      <OrderProductConfirmProductContainer>
        <Modal.Row style={styles.notLoadedViewContainer}>
          <NotFoundView
            withoutFooter
            description={
              baseProduct && !baseProduct.isDeepLinkingEnabled
                ? formatMessage(messages.orderProductNotAvailable)
                : undefined
            }
          />
        </Modal.Row>
      </OrderProductConfirmProductContainer>
    );
  }

  return (
    <OrderProductConfirmProductContainer>
      <OrderProductConfirmProductLoaded baseProduct={baseProduct} />
    </OrderProductConfirmProductContainer>
  );
};

// ─── Subcomponents ───────────────────────────────────────────────────────────

const OrderProductConfirmProductContainer = (props: Pick<ViewProps, 'children'>) => {
  const navigateBack = useNavigateBack();
  const { match, minWidth } = useResponsive();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const dismissModal = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      fitHeight
      size={match(['full', 'medium'])}
      onClose={dismissModal}
      mobileEdges={['right', 'bottom', 'left']}
    >
      {minWidth.isSM ? <Modal.FloatingCloseBtn onPress={dismissModal} /> : null}

      {props.children}
    </TransparentModal>
  );
};

const OrderProductConfirmProductLoaded = (props: OrderProductConfirmProductLoadedProps) => {
  const { baseProduct } = props;

  const { productSlug } = useSafeRouteParams<{ productSlug?: string }>();

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { formatMessage } = useIntl();
  const { match, currentBreakpoint, minWidth } = useResponsive();
  const { fetchLastValidOrder, isFetchingOrders } = useLastValidOrder();
  const lastInteractedStore = useLastInteractedStore();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleContinuePress = useCallback(() => {
    const screen = lastInteractedStore
      ? 'OrderProductConfirmLocation'
      : 'OrderProductSearchLocation';

    if (isExpoRouterEnabled) {
      const path = lastInteractedStore
        ? '/order-product/[productSlug]/confirm-location'
        : '/order-product/[productSlug]/search-location';

      router?.navigate({
        pathname: path,
        params: { productSlug: productSlug ?? '' },
      });
      return;
    }

    navigation?.navigate('Modal', {
      screen,
      params: { productSlug },
    });
  }, [isExpoRouterEnabled, navigation, router, productSlug, lastInteractedStore]);

  const descriptionSize = currentBreakpoint.isXS ? 4 : 3;
  const productCardContainerStyles = [
    styles.productCardContainer,
    match([styles.productCardContainerXS, styles.productCardContainerSM]),
  ];

  // ─── Effects ─────────────────────────────────────────────────────────

  useSafeFocusEffect(fetchLastValidOrder); // fetch the last placed order on mount

  useTrackEffect('deep-linking.start');

  return (
    <>
      {baseProduct.heading ? (
        <Modal.Headline size={match([1, 2, 3])} style={match([undefined, styles.headerSM])}>
          {baseProduct.heading}
        </Modal.Headline>
      ) : null}

      <Modal.Row style={styles.contentContainer}>
        {baseProduct.subheading ? (
          <BodyText size={descriptionSize}>{baseProduct.subheading}</BodyText>
        ) : null}

        <View style={productCardContainerStyles}>
          <ProductCardV2.Container canHover={false}>
            {baseProduct.asset?.url ? (
              <ProductCardV2.Image source={baseProduct.asset.url} alt={baseProduct.name} />
            ) : null}

            <ProductCardV2.LabelContainer>
              {baseProduct.isSeasonal ? (
                <ProductCardV2.Label>
                  {formatMessage(messages.confirmProductSeasonal)}
                </ProductCardV2.Label>
              ) : null}

              {baseProduct.name ? (
                <ProductCardV2.Heading>{baseProduct.name}</ProductCardV2.Heading>
              ) : null}

              {baseProduct.description ? (
                <ProductCardV2.Description>{baseProduct.description}</ProductCardV2.Description>
              ) : null}
            </ProductCardV2.LabelContainer>
          </ProductCardV2.Container>
        </View>
      </Modal.Row>

      <Modal.Footer
        style={match([styles.footerXS, styles.footerSM])}
        withoutTopBorder={minWidth.isSM}
      >
        <Button
          size="large"
          disabled={isFetchingOrders}
          onPress={handleContinuePress}
          testID="order-product.confirm-product-continue"
          accessibilityLabel={formatMessage(messages.orderProductContinue)}
          accessibilityHint={formatMessage(messages.orderProductContinue)}
        >
          {formatMessage(messages.orderProductContinue)}
        </Button>
      </Modal.Footer>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderProductNotAvailable: {
    defaultMessage: 'That item is no longer available.',
    description: 'Order Product | Not Available',
  },
  confirmProductSeasonal: {
    defaultMessage: 'Seasonal',
    description: 'Confirm Product | Seasonal Label',
  },
  orderProductContinue: {
    defaultMessage: 'Order now',
    description: 'Order Product | Continue',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerSM: {
    marginTop: theme.spacing['2'],
    paddingTop: theme.spacing['10'],
  },
  contentContainer: {
    flex: 1,
  },
  notLoadedViewContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: theme.spacing['6'],
  },
  productCardContainer: {
    paddingTop: theme.spacing['6'],
    width: '100%',
    alignSelf: 'center',
  },
  productCardContainerXS: {
    maxWidth: 400,
  },
  productCardContainerSM: {
    maxWidth: 342,
  },
  footerXS: {
    paddingVertical: theme.spacing['4'],
  },
  footerSM: {
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['10'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderProductConfirmProductLoadedProps = Readonly<{
  baseProduct: Exclude<
    ReturnType<typeof useOrderProductConfirmProductScreenData>['baseProduct'],
    null
  >;
}>;
